import { useState, useCallback } from "react";
import flotteService from "../../../../services/flotteService";
import { fieldConfigs } from "./FlotteSimulator/simulatorUtils";

const useSimulationEnMasse = (data, { onComplete } = {}) => {
  const [formData, setFormData] = useState({
    duree: "",
    simulationType: "simulation",
  });
  const [simulationResults, setSimulationResults] = useState(
    data.map((item) => ({
      ...item,
      simulation: null,
      status: "pending", // pending, loading, success, error
      error: null,
    })),
  );
  const [isSimulating, setIsSimulating] = useState(false);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!formData.duree) {
        alert("Veuillez saisir la durée");
        return;
      }

      // Vérifier que tous les véhicules ont un km_reel pour la simulation prorata km
      if (formData.simulationType === "simulation_prorata_km") {
        const vehiclesSansKm = simulationResults.filter(
          (v) => v.km_reel === undefined || v.km_reel === null,
        );
        if (vehiclesSansKm.length > 0) {
          const names = vehiclesSansKm
            .map(
              (v) =>
                `${v.vehicleInfo.marque} ${v.vehicleInfo.modele} (${v.vehicleInfo.immatriculation})`,
            )
            .join("\n- ");
          alert(
            `Impossible de lancer la simulation prorata km : les véhicules suivants n'ont pas de dernier km renseigné :\n\n- ${names}`,
          );
          return;
        }
      }

      setIsSimulating(true);

      // Réinitialiser les résultats
      setSimulationResults((prev) =>
        prev.map((item) => ({
          ...item,
          simulation: null,
          status: "loading",
          error: null,
        })),
      );

      // Traiter chaque véhicule séquentiellement
      for (let i = 0; i < simulationResults.length; i++) {
        const vehicleData = simulationResults[i];

        try {
          // Chercher le mouvement simulation existant correspondant au type choisi
          const existingSimulationMovement = vehicleData.mouvements?.find(
            (m) => m.type === formData.simulationType,
          );

          // Utiliser le mouvement simulation existant s'il y en a un, sinon fallback sur le dernier mouvement
          const baseMovement =
            existingSimulationMovement || vehicleData.lastMovement;

          // Préparer les données de projection avec les paramètres du formulaire
          const projection = {
            ...baseMovement,
            duree: parseInt(formData.duree),
            mouvement: formData.simulationType,
            type: formData.simulationType,
          };

          // Appel API /recalculer pour persister les résultats
          const simulationData = await flotteService.updateProjection({
            projection,
            item: { id: vehicleData.vehicleId },
            modifiedField: "duree",
          });

          // Mettre à jour le résultat pour ce véhicule spécifique
          setSimulationResults((prev) =>
            prev.map((item, index) =>
              index === i
                ? {
                    ...item,
                    simulation: simulationData,
                    status: "success",
                  }
                : item,
            ),
          );
        } catch (error) {
          console.error(
            `Erreur simulation véhicule ${vehicleData.vehicleId}:`,
            error,
          );

          // Mettre à jour avec l'erreur pour ce véhicule spécifique
          setSimulationResults((prev) =>
            prev.map((item, index) =>
              index === i
                ? {
                    ...item,
                    status: "error",
                    error: error.message || "Erreur inconnue",
                  }
                : item,
            ),
          );
        }
      }

      setIsSimulating(false);

      // Rafraîchir la liste principale pour refléter les données persistées
      if (onComplete) {
        await onComplete();
      }
    },
    [formData, simulationResults, onComplete],
  );

  // Fonctions utilitaires pour les comparaisons
  const getOriginalValue = useCallback((result, fieldKey) => {
    const originalKey = fieldKey;
    if (!originalKey) return null;

    return result.lastMovement[originalKey];
  }, []);

  const calculateDifference = useCallback((currentValue, originalValue) => {
    if (!originalValue || !currentValue) return null;
    const diff = currentValue - originalValue;
    // Tolérance pour les erreurs de précision en virgule flottante
    return Math.abs(diff) < 0.01 ? 0 : diff;
  }, []);

  const calculatePercentage = useCallback((currentValue, originalValue) => {
    if (!originalValue || originalValue === 0 || !currentValue) return null;
    return ((currentValue - originalValue) / Math.abs(originalValue)) * 100;
  }, []);

  const formatValue = useCallback((value, fieldKey) => {
    const config = fieldConfigs[fieldKey];
    if (!config || value === null || value === undefined) return "-";
    return config.formatter ? config.formatter(value) : value;
  }, []);

  const getStatusIcon = useCallback((status) => {
    switch (status) {
      case "loading":
        return <i className="fas fa-spinner fa-spin text-primary"></i>;
      case "success":
        return <i className="fas fa-check-circle text-success"></i>;
      case "error":
        return <i className="fas fa-times-circle text-danger"></i>;
      default:
        return <i className="fas fa-clock text-muted"></i>;
    }
  }, []);

  return {
    formData,
    setFormData,
    simulationResults,
    isSimulating,
    handleInputChange,
    handleSubmit,
    getOriginalValue,
    calculateDifference,
    calculatePercentage,
    formatValue,
    getStatusIcon,
  };
};

export default useSimulationEnMasse;
